import { useEffect, useState } from 'react'
import { SyncDesktopIcon } from '@/components/icons'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import {
  usePushWorkItemsToDevOps,
  useWorkItemAzureDevOpsProjects,
  useWorkItemAzureDevOpsEpics,
  useWorkItemAzureDevOpsFeatures,
} from '@/hooks'
import type { WorkItem } from '@/types'

interface PushToDevOpsDialogProps {
  projectId: string
  workItems: WorkItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onPushed?: () => void
}

const NONE_VALUE = 'none'

function formatNumber(num: number | null): string {
  if (num === null) return '—'
  return `WI-${String(num).padStart(3, '0')}`
}

export function PushToDevOpsDialog({ projectId, workItems, open, onOpenChange, onPushed }: PushToDevOpsDialogProps) {
  const pushMutation = usePushWorkItemsToDevOps(projectId)
  const { data: projects = [], isLoading: projectsLoading } = useWorkItemAzureDevOpsProjects(projectId, open)

  const [destinationProject, setDestinationProject] = useState<string>('')
  const [selectedEpicId, setSelectedEpicId] = useState<string>('')
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>('')

  const epicIdNumber = selectedEpicId ? Number(selectedEpicId) : null

  const { data: epics = [] } = useWorkItemAzureDevOpsEpics(projectId, destinationProject, open)
  const { data: features = [] } = useWorkItemAzureDevOpsFeatures(projectId, destinationProject, epicIdNumber, open)

  useEffect(() => {
    if (!open) {
      setDestinationProject('')
      return
    }
    if (projects.length > 0 && !projects.some(p => p.name === destinationProject)) {
      setDestinationProject(projects[0].name)
    }
  }, [open, projects, destinationProject])

  const handleDestinationChange = (value: string) => {
    setDestinationProject(value)
    setSelectedEpicId('')
    setSelectedFeatureId('')
  }

  const handleEpicChange = (value: string) => {
    setSelectedEpicId(value === NONE_VALUE ? '' : value)
    setSelectedFeatureId('')
  }

  const parentWorkItemId = selectedFeatureId
    ? Number(selectedFeatureId)
    : epicIdNumber ?? undefined

  const handlePush = async () => {
    if (workItems.length === 0 || !destinationProject) return
    try {
      const result = await pushMutation.mutateAsync({
        workItemIds: workItems.map(w => w.id),
        destinationProject,
        parentWorkItemId,
      })
      const total = result.pushed.length + result.failed.length
      if (result.failed.length === 0) {
        toast.success(`Pushed ${result.pushed.length} item${result.pushed.length === 1 ? '' : 's'} to Azure DevOps`)
      } else if (result.pushed.length === 0) {
        toast.error(result.failed[0]?.errorMessage ?? `Failed to push ${result.failed.length} item${result.failed.length === 1 ? '' : 's'}`)
      } else {
        toast.warning(`Pushed ${result.pushed.length} of ${total}. ${result.failed.length} failed.`)
      }
      onOpenChange(false)
      onPushed?.()
    } catch {
      toast.error('Failed to push to Azure DevOps')
    }
  }

  const noProjects = !projectsLoading && projects.length === 0

  const showParentSection = !!destinationProject && epics.length > 0
  const showFeatureSelect = !!epicIdNumber && features.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]" data-testid="push-to-devops-dialog">
        <DialogHeader>
          <DialogTitle>Push to Azure DevOps</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <label htmlFor="push-to-devops-destination" className="text-sm font-medium">
            Destination in Azure DevOps
          </label>
          <Select value={destinationProject} onValueChange={handleDestinationChange} disabled={noProjects}>
            <SelectTrigger id="push-to-devops-destination" data-testid="push-to-devops-destination">
              <SelectValue placeholder={projectsLoading ? 'Loading projects…' : 'Select a project'} />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.name}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {noProjects ? (
            <p role="alert" className="text-xs text-destructive">
              No Azure DevOps projects available. Check the Azure DevOps connector.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {destinationProject
                ? <>Work items will be created in project <span className="font-medium text-foreground">{destinationProject}</span>. An IDSP back-reference will be added to each work item&apos;s description for traceability.</>
                : 'An IDSP back-reference will be added to each work item’s description for traceability.'}
            </p>
          )}
        </div>

        {showParentSection && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="push-to-devops-epic" className="text-sm font-medium">
                Epic
              </label>
              <Select value={selectedEpicId || NONE_VALUE} onValueChange={handleEpicChange}>
                <SelectTrigger id="push-to-devops-epic" data-testid="push-to-devops-epic" aria-label="Epic">
                  <SelectValue placeholder="No parent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>No parent</SelectItem>
                  {epics.map(e => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {e.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showFeatureSelect && (
              <div className="space-y-2">
                <label htmlFor="push-to-devops-feature" className="text-sm font-medium">
                  Feature
                </label>
                <Select
                  value={selectedFeatureId || NONE_VALUE}
                  onValueChange={v => setSelectedFeatureId(v === NONE_VALUE ? '' : v)}
                >
                  <SelectTrigger id="push-to-devops-feature" data-testid="push-to-devops-feature" aria-label="Feature">
                    <SelectValue placeholder="No feature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>No feature</SelectItem>
                    {features.map(f => (
                      <SelectItem key={f.id} value={String(f.id)}>
                        {f.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {workItems.length} {workItems.length === 1 ? 'item' : 'items'} will be pushed:
        </p>

        <div className="space-y-2 max-h-[260px] overflow-y-auto">
          {workItems.map(item => (
            <div
              key={item.id}
              className="flex items-center gap-3 rounded-lg border p-3"
              data-testid={`push-to-devops-item-${item.id}`}
            >
              <div className="w-20 shrink-0 text-sm text-muted-foreground font-medium">
                {formatNumber(item.number)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.title}</p>
                {item.feature && <p className="text-xs text-muted-foreground truncate">{item.feature}</p>}
              </div>
              <WorkItemStatusBadge status={item.status} />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handlePush}
            disabled={pushMutation.isPending || workItems.length === 0 || !destinationProject}
            data-testid="push-to-devops-confirm"
          >
            <SyncDesktopIcon className="h-4 w-4" />
            {pushMutation.isPending ? 'Pushing...' : 'Push to Azure DevOps'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
